import { makeStyles } from "@material-ui/core/styles";
import { Box, Card, CardContent, Typography } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { TFunction } from "i18next";
import AddTestToAssessment from "./AddTestToAssessment";
import {
    extractDataFromJobSK1AssessmentGroup,
    useDeleteAssessmentOrTest,
    useCountAssessmentRequestsByAssessment
} from "./helper";
import AssessmentTestList from "./AssessmentTestList";
import StageRulesCard from "./StageRulesCard";
import ButtonIconTooltipCompleo from "Components/ButtonIconTooltipCompleo";
import useJobContext from "../../useJobContext";
import ModalJobAssessmentsGroup from "../ModalJobAssessmentsGroup/Index";
import React from "react";
import { useGlobalDialog } from "_ReactContext/GlobalDialogContext";

const useStyles = makeStyles((theme) => ({
    root: {
        margin: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        width: "100%"
    },
    content: {
        flex: 1,
        display: "flex",
        flexDirection: "column"
    },
    actions: {
        display: "flex",
        justifyContent: "flex-end"
    },
    expandIcon: {
        marginLeft: "auto"
    }
}));

interface IAssessmentGroup {
    item: CompleoShared.Assessments.IAssessment;
    tests: CompleoShared.Assessments.ITest[];
    tLocal: TFunction;
}
const Assessment = (props: IAssessmentGroup) => {
    const { item, tLocal, tests } = props;
    const [data] = useJobContext();
    const [modalOpen, setModalOpen] = React.useState(false);
    const { callDialog } = useGlobalDialog();

    const classes = useStyles();
    const assessmentId = extractDataFromJobSK1AssessmentGroup(
        item.skGS1pk,
        "assessmentId"
    );

    // Issue #248 — fetch active request count for this assessment+job so we
    // can warn the recruiter before removing or editing it.
    const {
        count: activeRequestCount,
        hasActiveRequests,
        ready: requestCountReady
    } = useCountAssessmentRequestsByAssessment(
        data?.JobId || "",
        assessmentId || ""
    );

    const {
        deleteFN: handleDelete,
        handleDeleteMain,
        showMessageFullControl
    } = useDeleteAssessmentOrTest(tLocal);

    /**
     * Edit button handler with optional active-request warning.
     * If candidates have already been sent this assessment, show a
     * confirmation dialog before opening the edit modal.
     */
    const handleEditClick = () => {
        if (requestCountReady && hasActiveRequests) {
            callDialog({
                title: tLocal("COMPLEOGENERAL_Edit"),
                bodyText: tLocal("msgEditAssessmentGroupWithRequests", {
                    count: activeRequestCount
                }),
                agreeButtonText: tLocal("COMPLEOGENERAL_YES"),
                disagreeButtonText: tLocal("COMPLEOGENERAL_NO"),
                agreeFunction: async () => {
                    setModalOpen(true);
                },
                disagreeFunction: () => {}
            });
        } else {
            setModalOpen(true);
        }
    };

    /**
     * Delete button handler with optional active-request warning.
     * If candidates have already been sent this assessment, show an
     * enhanced warning dialog instead of the generic "Are you sure?".
     * After confirmation the actual delete is performed via handleDeleteMain
     * (skips the inner generic dialog from deleteFN).
     */
    const handleDeleteClick = async () => {
        if (requestCountReady && hasActiveRequests) {
            callDialog({
                title: tLocal("COMPLEOGENERAL_Delete"),
                bodyText: tLocal("msgDeleteAssessmentGroupWithRequests", {
                    count: activeRequestCount
                }),
                agreeButtonText: tLocal("COMPLEOGENERAL_YES"),
                disagreeButtonText: tLocal("COMPLEOGENERAL_NO"),
                agreeFunction: async () => {
                    await handleDeleteMain(item, data?.JobId, true);
                },
                disagreeFunction: () => {}
            });
        } else {
            // No active requests — use the standard flow (generic confirm dialog)
            await handleDelete(item, data?.JobId, true);
        }
    };

    return (
        <Card className={classes.root}>
            <CardContent className={classes.content}>
                <Box
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >
                    <Typography
                        variant="h6"
                        component="h2"
                        gutterBottom
                        style={{ fontWeight: 700 }}
                    >
                        {item.name}
                    </Typography>
                    <Box flexGrow={1} />
                    <ButtonIconTooltipCompleo
                        label="Edit"
                        onClick={handleEditClick}
                        disabled={!requestCountReady}
                    >
                        <Edit />
                    </ButtonIconTooltipCompleo>
                    {tests.length === 0 && (
                        <ButtonIconTooltipCompleo
                            label="Delete"
                            onClick={handleDeleteClick}
                            disabled={!requestCountReady}
                        >
                            <Delete color="secondary" />
                        </ButtonIconTooltipCompleo>
                    )}
                    <AddTestToAssessment
                        tLocal={tLocal}
                        assessmentId={assessmentId || ""}
                        tests={tests}
                    />
                </Box>
                <StageRulesCard
                    resultsToStageRule={item.resultsToStageRule}
                    tLocal={tLocal}
                />
                <AssessmentTestList tLocal={tLocal} tests={tests} />
                {showMessageFullControl.MessageElement}
            </CardContent>
            {modalOpen && (
                <ModalJobAssessmentsGroup
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    editData={item}
                />
            )}
        </Card>
    );
};

export default Assessment;
